import { createSlice} from "@reduxjs/toolkit";

import { getSofDocument, deleteSofDocumentThunk, createSofDocsThunk, updateCsofThunk, formatOverrideThunk, regenerateSofDocumentThunk, regenerateSofDocumentsThunk } from "../thunks/sofDocumentThunk";

/** Human-friendly labels for CSOF pipeline stages (used in notifications) */
const STAGE_NOTIF_LABELS: Record<string, string> = {
  created: "Record Created",
  uploading: "Uploading Document",
  textract_started: "Analyzing Document",
  textract_done: "Analysis Complete",
  format_detected: "Format Detected",
  header_extracting: "Extracting Data",
  entries_parsed: "Data Extracted",
  parsing: "Detecting Format",
  db_saving: "Saving Data",
  template_generating: "Preparing Template",
  pdf_generating: "Generating PDF",
  s3_linking: "Finalizing Document",
  format_unknown: "Format Unknown — Use Override",
};

const stageFriendly = (stage: string): string =>
  STAGE_NOTIF_LABELS[stage] || stage;

interface CsofNotification {
  id: number;
  message: string;
  severity: "info" | "success" | "error" | "warning";
}

interface SofDocumentState {
  documents: any[];
  total: number;
  loading: boolean;
  error: string | null;
  jobList: any[];
  processingCsofId: string | null;
  processingStage: string | null;
  processingMessage: string | null;
  refreshTrigger: number;
  notifications: CsofNotification[];
  regenerationResults: any[];
  regeneratingGroups: boolean;
}

let notifCounter = 0;

const initialState: SofDocumentState = {
  documents: [],
  total: 0,
  loading: false,
  error: null,
  jobList: [],
  processingCsofId: null,
  processingStage: null,
  processingMessage: null,
  refreshTrigger: 0,
  notifications: [],
  regenerationResults: [],
  regeneratingGroups: false,
};

const sofDocumentSlice = createSlice({
  name: "sofDocument",
  initialState,
  reducers: {
    setCsofProgress(state, action) {
      const { csof_id, stage, message } = action.payload;
      state.processingCsofId = csof_id;
      state.processingStage = stage;
      state.processingMessage = message;
      // Update the status in documents list if it exists
      const doc = state.documents.find((d) => d.csof_id === csof_id);
      if (doc) {
        doc.status = stage;
      }
      // Refresh documents from server on every progress event
      state.refreshTrigger += 1;
      // Push notification for this stage
      notifCounter += 1;
      state.notifications.push({
        id: notifCounter,
        message: stageFriendly(stage),
        severity: "info",
      });
    },
    setCsofComplete(state, action) {
      const { csof_id, data, ...rest } = action.payload;
      state.processingCsofId = null;
      state.processingStage = null;
      state.processingMessage = null;
      // Update the document in the list with the completed data
      // Backend may send completed fields in `data` or at the top level
      const mergeData = data || rest;
      const idx = state.documents.findIndex((d) => d.csof_id === csof_id);
      if (idx !== -1) {
        state.documents[idx] = { ...state.documents[idx], ...mergeData, status: "completed" };
      }
      // Signal components to do a full refresh from server
      state.refreshTrigger += 1;
      notifCounter += 1;
      state.notifications.push({
        id: notifCounter,
        message: "SOF document processed successfully!",
        severity: "success",
      });
    },
    setCsofFailed(state, action) {
      const { csof_id, message } = action.payload;
      state.processingCsofId = null;
      state.processingStage = null;
      state.processingMessage = null;
      const doc = state.documents.find((d) => d.csof_id === csof_id);
      if (doc) {
        doc.status = "failed";
        doc.error = message;
      }
      // Signal components to do a full refresh from server
      state.refreshTrigger += 1;
      notifCounter += 1;
      state.notifications.push({
        id: notifCounter,
        message: `SOF processing failed${message ? ": " + message : ""}`,
        severity: "error",
      });
    },
    dismissNotification(state, action) {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    setCsofFormatUnknown(state, action) {
      const { csof_id, message } = action.payload;
      state.processingCsofId = null;
      state.processingStage = null;
      state.processingMessage = null;
      const doc = state.documents.find((d) => d.csof_id === csof_id);
      if (doc) {
        doc.status = "format_unknown";
      }
      state.refreshTrigger += 1;
      notifCounter += 1;
      state.notifications.push({
        id: notifCounter,
        message: message || "Could not detect document format — please use Override Format",
        severity: "warning",
      });
    },
    clearCsofProcessing(state) {
      state.processingCsofId = null;
      state.processingStage = null;
      state.processingMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSofDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSofDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload?.data || [];
        state.total = action.payload.total;
      })
      .addCase(getSofDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSofDocumentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSofDocumentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = state.documents.filter(
          (doc) => doc.csof_id !== action.meta?.arg?.csofId
        );
      })
      .addCase(deleteSofDocumentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      builder
      .addCase(createSofDocsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSofDocsThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Optimistically add the new record to the documents list
        const newDoc = action.payload?.data;
        if (newDoc?.csof_id) {
          const params = action.meta.arg;
          state.documents.push({
            ...newDoc,
            job_id: params.job_id,
            cargo_id: params.cargo_id,
            berth_id: params.berth_id,
            status: newDoc.status || "created",
          });
          state.total += 1;
        }
      })
      .addCase(createSofDocsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      builder
      .addCase(updateCsofThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCsofThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Optimistically update the document with upload info
        const updatedCsofId = action.meta.arg.csofId;
        const idx = state.documents.findIndex((d) => d.csof_id === updatedCsofId);
        if (idx !== -1) {
          state.documents[idx] = {
            ...state.documents[idx],
            filename: action.meta.arg.key,
            status: "uploading",
          };
        }
      })
      .addCase(updateCsofThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      builder
      .addCase(formatOverrideThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(formatOverrideThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload?.data;
        if (updated?.csof_id) {
          const idx = state.documents.findIndex((d) => d.csof_id === updated.csof_id);
          if (idx !== -1) {
            state.documents[idx] = { ...state.documents[idx], company_format: updated.company_format };
          }
        }
      })
      .addCase(formatOverrideThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      builder
      .addCase(regenerateSofDocumentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateSofDocumentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload?.data;
        const csofId = action.meta.arg.csofId;
        const idx = state.documents.findIndex((doc) => doc.csof_id === csofId);
        if (idx !== -1) {
          state.documents[idx] = {
            ...state.documents[idx],
            ...updated,
            entry_cnt: updated?.entry_count ?? state.documents[idx].entry_cnt,
            status: "completed",
          };
        }
      })
      .addCase(regenerateSofDocumentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      builder
      .addCase(regenerateSofDocumentsThunk.pending, (state) => {
        state.error = null;
        state.regeneratingGroups = true;
        state.regenerationResults = [];
      })
      .addCase(regenerateSofDocumentsThunk.fulfilled, (state, action) => {
        state.regeneratingGroups = false;
        state.regenerationResults = action.payload?.data?.results || [];
        state.refreshTrigger += 1;
      })
      .addCase(regenerateSofDocumentsThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        state.regeneratingGroups = false;
      });
  },
});

export const {
  setCsofProgress,
  setCsofComplete,
  setCsofFailed,
  setCsofFormatUnknown,
  dismissNotification,
  clearCsofProcessing,
} = sofDocumentSlice.actions;

export default sofDocumentSlice.reducer;
